// @packages
import Paper from '@material-ui/core/Paper';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';

// @scripts
import { getFeatures } from './config/features';
import { getSelectRowColumn, getColumns, getTemplate } from './config/columns';
import { handleOnHeaderCellRendered, handleOnRowPanelRendered, handleOnRowsRendered } from './config/events';
import {
    DEFAULT_WIDTH_COLUMN,
    exportToExcel,
    importExcel,
    getJQueryElement,
    summaryType,
    widthType,
    exportScheduleGridToExcel
} from './config/helpers';

// @styles
import styles from './styles';

const CtrlIgGrid = ({
    checkedItems,
    className,
    classes,
    columns,
    dataSource,
    details,
    enableCheckBox,
    enableFilter,
    enableHiding,
    enableHotkeys,
    enableMergeCells,
    enableMoveColumn,
    enableMultiColumHeader,
    enablePaging,
    enableResize,
    enableRowIndex,
    enableSelectRow,
    enableSort,
    enableSummary,
    enableTooltip,
    enableUpdate,
    fixedHeaders,
    groupByKey,
    headerLabels,
    height,
    id,
    isMerged,
    onCheckItemChanged,
    onColumnMoving,
    onColumnSorting,
    onDataFiltered,
    onDataFiltering,
    onEditCellEnded,
    onEditCellStarting,
    onSelectRow,
    onTooltipShowing,
    primaryKey,
    visible,
    width,
    widthType
}) => {
    // if (!visible) {
    //     return null;
    // }
    const tableRef = useRef();

    // When some column has rowPanelRender, switch igGrid from normal to igHierarchicalGrid.
    const hasRowPanelRender = columns.some((item) => item.rowPanelRender);

    // When some column has 'isFixed' as true, enable ColumnFixing feature for the IG Grid.
    const enableFixColumn = columns.some((item) => item.isFixed);
    const enableGroup = groupByKey !== null;
    const getItemById = (id) => dataSource.find((item) => item[primaryKey] === id);
    const getItemIndexById = (id) => {
        let index = 0;
        while (dataSource[index][primaryKey] !== id) {
            index += 1;
        }
        return index;
    };
    const selectRowColumn = getSelectRowColumn({
        enableFixColumn,
        enableSelectRow,
        onSelectRow,
        widthType
    });

    // Function to expand or collapse the inline row panel if has rowPanelRender.
    const onToggleRowExpand = (parentRow) => getJQueryElement(tableRef.current).igHierarchicalGrid('toggle', parentRow);
    const formatedColumns = [...selectRowColumn, ...getColumns(columns).map((column) => Object.assign(column, {
        headerText: column.headerText || headerLabels[column.key] || column.key,
        template: getTemplate({ column, onToggleRowExpand }),
        width: column.width || DEFAULT_WIDTH_COLUMN
    }))];

    const onGetFeatures = () => {
        const featureProps = {
            enableCheckBox,
            enableFilter,
            enableFixColumn,
            enableGroup,
            enableHiding,
            enableHotkeys,
            enableMergeCells,
            enableMoveColumn,
            enableMultiColumHeader,
            enablePaging,
            enableResize,
            enableRowIndex,
            enableSort,
            enableSummary,
            enableTooltip,
            enableUpdate
        };
        const options = {
            columns: formatedColumns,
            dataSource,
            getItemById,
            getItemIndexById,
            groupByKey,
            isMerged,
            jQueryGrid: getJQueryElement(tableRef.current),
            primaryKey
        };
        const events = {
            onCheckItemChanged,
            onColumnMoving,
            onColumnSorting,
            onDataFiltered,
            onDataFiltering,
            onEditCellStarting,
            onEditCellEnded,
            onTooltipShowing
        };
        return getFeatures(featureProps, options, events);
    };

    const initializeIgGrid = () => {
        const jQueryGrid = getJQueryElement(tableRef.current);
        const features = onGetFeatures();
        const options = {
            autoGenerateColumns: false,
            columns: formatedColumns,
            dataSource,
            features,
            fixedHeaders,
            height,
            primaryKey,
            rowsRendered: (_event, ui) => handleOnRowsRendered({
                checkedItems,
                jQueryGrid,
                ui,
                primaryKey
            }),
            rendered: function(evt, ui) {
                getJQueryElement(ui.owner.element).data("igGridFiltering")._editors[4]._setOption("maxDecimals", 3);
            },
            width,
            widthType
        };
        if (hasRowPanelRender || details) {
            const columnLayout = details || {
                autoGenerateColumns: false,
                columns: [{ template: '<div data-render-template />' }],
                key: primaryKey,
                rowsRendered: (_evt, ui) => handleOnRowPanelRendered({
                    classes,
                    columns,
                    dataSource,
                    primaryKey,
                    ui
                })
            };
            const rowPanelRenderOptions = {
                autoGenerateLayouts: false,
                columnLayouts: [Object.assign(columnLayout, details && {
                    headerCellRendered: (_evt, ui) => handleOnHeaderCellRendered({ headerLabels, ui })
                })],
                initialDataBindDepth: 1
            };
            jQueryGrid.igHierarchicalGrid(Object.assign(options, rowPanelRenderOptions));
        } else {
            jQueryGrid.igGrid(options);
        }
    };

    const destroyIgGrid = (jQueryGrid) => hasRowPanelRender || details
        ? jQueryGrid.igHierarchicalGrid('destroy')
        : jQueryGrid.igGrid('destroy');

    useEffect(() => {
        initializeIgGrid();
        let observerRefValue = null;
        if (tableRef.current) {
            observerRefValue = tableRef.current;
          }
        return () => destroyIgGrid(getJQueryElement(observerRefValue));
    }, [dataSource, checkedItems]);

    const tableClass = classNames(
        className,
        classes.table,
        {
            [classes.hiddenExpandColumnWithoutGrouping]: !details && hasRowPanelRender && !enableGroup,
            [classes.hiddenExpandColumnWithGrouping]: !details && hasRowPanelRender && enableGroup
        }
    );

    return (
        <Paper className={tableClass} elevation={2} id={id}>
            <table id={`${id}-table`} ref={tableRef} />
        </Paper>
    );
};

CtrlIgGrid.propTypes = {
    checkedItems: PropTypes.arrayOf(PropTypes.object),
    className: PropTypes.string,
    classes: PropTypes.object.isRequired,
    columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
        allowFiltering: PropTypes.bool,
        allowFixing: PropTypes.bool,
        allowHiding: PropTypes.bool,
        allowMerge: PropTypes.bool,
        allowMoving: PropTypes.bool,
        allowResizing: PropTypes.bool,
        allowSorting: PropTypes.bool,
        allowSummaries: PropTypes.bool,
        allowTooltips: PropTypes.bool,
        dataType: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
        editorOptions: PropTypes.object,
        editorType: PropTypes.oneOf(['combo', 'datepicker', undefined]),
        headerText: PropTypes.string,
        hidden: PropTypes.bool,
        isFixed: PropTypes.bool,
        key: PropTypes.string.isRequired,
        readOnly: PropTypes.bool,
        rowPanelRender: PropTypes.func,
        summary: PropTypes.shape({
            calculator: PropTypes.func,
            type: PropTypes.oneOf([summaryType.CUSTOM, summaryType.SUM])
        }),
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })])).isRequired,
    dataSource: PropTypes.array.isRequired,
    details: PropTypes.shape({
        autoGenerateColumns: PropTypes.bool.isRequired,
        columns: PropTypes.array,
        key: PropTypes.string.isRequired,
        primaryKey: PropTypes.string.isRequired
    }),
    enableCheckBox: PropTypes.bool,
    enableFilter: PropTypes.bool,
    enableHiding: PropTypes.bool,
    enableHotkeys: PropTypes.bool,
    enableMergeCells: PropTypes.bool,
    enableMoveColumn: PropTypes.bool,
    enableMultiColumHeader: PropTypes.bool,
    enablePaging: PropTypes.bool,
    enableResize: PropTypes.bool,
    enableRowIndex: PropTypes.bool,
    enableSelectRow: PropTypes.bool,
    enableSort: PropTypes.bool,
    enableSummary: PropTypes.bool,
    enableTooltip: PropTypes.bool,
    enableUpdate: PropTypes.bool,
    fixedHeaders: PropTypes.bool,
    groupByKey: PropTypes.string,
    headerLabels: PropTypes.object,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    id: PropTypes.string.isRequired,
    isMerged: PropTypes.bool,
    onCheckItemChanged: PropTypes.func,
    onColumnMoving: PropTypes.func,
    onColumnSorting: PropTypes.func,
    onDataFiltered: PropTypes.func,
    onDataFiltering: PropTypes.func,
    onEditCellEnded: PropTypes.func,
    onEditCellStarting: PropTypes.func,
    onSelectRow: PropTypes.func,
    onTooltipShowing: PropTypes.func,
    primaryKey: PropTypes.string.isRequired,
    visible: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    widthType: PropTypes.oneOf([widthType.PIXEL, widthType.PERCENTAGE])
};

CtrlIgGrid.defaultProps = {
    checkedItems: [],
    className: null,
    details: null,
    enableCheckBox: false,
    enableFilter: false,
    enableHiding: false,
    enableHotkeys: false,
    enableMergeCells: false,
    enableMoveColumn: false,
    enableMultiColumHeader: false,
    enablePaging: false,
    enableResize: false,
    enableRowIndex: false,
    enableSelectRow: false,
    enableSort: false,
    enableSummary: false,
    enableTooltip: false,
    enableUpdate: false,
    fixedHeaders: true,
    groupByKey: null,
    headerLabels: {},
    height: null,
    isMerged: false,
    onCheckItemChanged: null,
    onColumnMoving: null,
    onColumnSorting: null,
    onDataFiltered: null,
    onDataFiltering: null,
    onEditCellEnded: null,
    onEditCellStarting: null,
    onSelectRow: null,
    onTooltipShowing: null,
    visible: true,
    width: '100%',
    widthType: widthType.PIXEL
};

export { exportToExcel, exportScheduleGridToExcel, importExcel };
export default withStyles(styles)(CtrlIgGrid);
